/**
 * Central color palette for UI theming.
 * Every UI color token used by the map style refactor is defined here.
 */

export interface UIColorPalette {

    /** Global brand primary color. */
    "brandPrimary": string;

    /** Generic app page background color (kept for future reuse). */
    "appPageBackground": string;

    /** Main floating overlay background (cards, menus). */
    "overlayCardBackground": string;

    /** Soft overlay variant for secondary zones. */
    "overlayCardBackgroundSoft": string;

    /** Strong overlay variant for top-level floating cards. */
    "overlayCardBackgroundStrong": string;

    /** Default border color for overlays. */
    "overlayBorder": string;

    /** Strong border color for selected/active states. */
    "overlayBorderStrong": string;

    /** Primary text color displayed on overlays. */
    "overlayTextPrimary": string;

    /** Secondary/muted text color displayed on overlays. */
    "overlayTextSecondary": string;

    /** Delay status text color (positive delay / late). */
    "statusDelayText": string;

    /** Advance status text color (negative delay / early). */
    "statusAdvanceText": string;

    /** On-time status text color when realtime confirms no delay. */
    "statusOnTimeText": string;

    /** Text color displayed on top of brand primary surfaces. */
    "textOnBrandPrimary": string;

    /** Active selector/button background in map-style panel. */
    "selectorActiveBackground": string;

    /** Strong active selector/button background in map-style panel. */
    "selectorActiveBackgroundStrong": string;

    /** Active selector/button text color in map-style panel. */
    "selectorActiveText": string;

    /** Idle selector/button background in map-style panel. */
    "selectorIdleBackground": string;

    /** Idle selector/button text color in map-style panel. */
    "selectorIdleText": string;

    /** Hover background for clickable list rows/actions. */
    "interactiveHoverBackground": string;

    /** Autocomplete item row background gradient. */
    "autocompleteItemBackgroundGradient": string;

    /** Autocomplete item row hover background gradient. */
    "autocompleteItemBackgroundGradientHover": string;

    /** Background used by bottom itinerary panel overlay. */
    "itineraryPanelBackground": string;

    /** Border and text accent for the itinerary-details timing pill in the panel chrome row. */
    "itineraryDetailsTimingPillAccent": string;

    /** Border and text accent for the itinerary-details offer-loading pill in the panel chrome row. */
    "itineraryDetailsOfferLoadingAccent": string;

    /** Outline accent for the personal-car (I take my car) price pill. */
    "myCarPricePillAccent": string;

    /** Background used by dynamic island overlay. */
    "dynamicIslandBackground": string;

    /** Floating action button background color. */
    "floatingActionButtonBackground": string;

    /** Floating action button attention state background (e.g. GPS waiting). */
    "floatingActionButtonAttentionBackground": string;

    /** Modal backdrop background color. */
    "modalBackdropBackground": string;

    /** Modal card background color. */
    "modalCardBackground": string;

    /** Modal card border color. */
    "modalCardBorder": string;

    /** Modal card primary text color. */
    "modalCardTextPrimary": string;

    /** SwissPass login button background. */
    "swissPassButtonBackground": string;

    /** SwissPass login button hover background. */
    "swissPassButtonBackgroundHover": string;

    /** Generic thumb color for sliders/toggles. */
    "controlThumb": string;
}

export interface ColorPalette {
    "light": UIColorPalette;
    "dark":  UIColorPalette;
}

export const colorPalette: ColorPalette = {
    "light": {
        "brandPrimary":                            "#11D39C",
        "appPageBackground":                       "rgba(255, 255, 255, 0.95)",
        "overlayCardBackground":                   "rgba(255, 255, 255, 0.7)",
        "overlayCardBackgroundSoft":               "rgba(255, 255, 255, 0.82)",
        "overlayCardBackgroundStrong":             "rgba(255, 255, 255, 0.98)",
        "overlayBorder":                           "rgba(15, 23, 42, 0.08)",
        "overlayBorderStrong":                     "rgba(15, 23, 42, 0.2)",
        "overlayTextPrimary":                      "#0F172A",
        "overlayTextSecondary":                    "#334155",
        "statusDelayText":                         "#7C3AED",
        "statusAdvanceText":                       "#B91C1C",
        "statusOnTimeText":                        "#0C9570",
        "textOnBrandPrimary":                      "#FFFFFF",
        "selectorActiveBackground":                "rgba(17, 211, 156, 0.15)",
        "selectorActiveBackgroundStrong":          "rgba(17, 211, 156, 0.28)",
        "selectorActiveText":                      "#0F172A",
        "selectorIdleBackground":                  "rgba(255, 255, 255, 0.66)",
        "selectorIdleText":                        "#0F172A",
        "interactiveHoverBackground":              "rgba(15, 23, 42, 0.06)",
        "autocompleteItemBackgroundGradient":      "linear-gradient(to right, #ffffff 0%, rgba(255,255,255,0.6) 60%, rgba(255,255,255,0) 100%)",
        "autocompleteItemBackgroundGradientHover": "linear-gradient(to right, rgb(241 245 249) 0%, rgba(241,245,249,0.6) 60%, rgba(241,245,249,0) 100%)",
        "itineraryPanelBackground":                "rgba(255, 255, 255, 0.4)",
        "itineraryDetailsTimingPillAccent":        "rgb(91, 87, 226)",
        "itineraryDetailsOfferLoadingAccent":      "#7C3AED",
        "myCarPricePillAccent":                    "#972727",
        "dynamicIslandBackground":                 "rgba(255, 255, 255, 0.55)",
        "floatingActionButtonBackground":          "white",
        "floatingActionButtonAttentionBackground": "rgba(239, 68, 68, 1)",
        "modalBackdropBackground":                 "rgba(100, 116, 139, 0.4)",
        "modalCardBackground":                     "rgba(255, 255, 255, 0.98)",
        "modalCardBorder":                         "rgba(15, 23, 42, 0.08)",
        "modalCardTextPrimary":                    "#0F172A",
        "swissPassButtonBackground":               "rgba(197, 20, 22, 1)",
        "swissPassButtonBackgroundHover":          "rgba(175, 22, 2, 1)",
        "controlThumb":                            "#FFFFFF",
    },
    "dark": {
        "brandPrimary":                            "#11D39C",
        "appPageBackground":                       "rgba(30, 58, 102, 0.95)",
        "overlayCardBackground":                   "rgba(30, 58, 102, 0.9)",
        "overlayCardBackgroundSoft":               "rgba(44, 74, 122, 0.82)",
        "overlayCardBackgroundStrong":             "rgba(22, 47, 86, 0.96)",
        "overlayBorder":                           "rgba(255, 255, 255, 0.14)",
        "overlayBorderStrong":                     "rgba(255, 255, 255, 0.22)",
        "overlayTextPrimary":                      "#F8FAFC",
        "overlayTextSecondary":                    "#D7E4FF",
        "statusDelayText":                         "#C4B5FD",
        "statusAdvanceText":                       "#F87171",
        "statusOnTimeText":                        "#6EE7C8",
        "textOnBrandPrimary":                      "#082B24",
        "selectorActiveBackground":                "rgba(17, 211, 156, 0.24)",
        "selectorActiveBackgroundStrong":          "rgba(17, 211, 156, 0.4)",
        "selectorActiveText":                      "#F8FAFC",
        "selectorIdleBackground":                  "rgba(30, 58, 102, 0.5)",
        "selectorIdleText":                        "#F8FAFC",
        "interactiveHoverBackground":              "rgba(255, 255, 255, 0.1)",
        "autocompleteItemBackgroundGradient":      "linear-gradient(to right, rgba(22, 47, 86, 0.9) 0%, rgba(22, 47, 86, 0.7) 60%, rgba(22, 47, 86, 0.2) 100%)",
        "autocompleteItemBackgroundGradientHover": "linear-gradient(to right, rgba(26, 57, 105, 0.9) 0%, rgba(26, 57, 105, 0.7) 60%, rgba(26, 57, 105, 0.2) 100%)",
        "itineraryPanelBackground":                "rgba(30, 58, 102, 0.45)",
        "itineraryDetailsTimingPillAccent":        "rgb(64, 160, 245)",
        "itineraryDetailsOfferLoadingAccent":      "#C4B5FD",
        "myCarPricePillAccent":                    "#F87171",
        "dynamicIslandBackground":                 "rgba(30, 58, 102, 0.55)",
        "floatingActionButtonBackground":          "rgba(22, 47, 86, 0.96)",
        "floatingActionButtonAttentionBackground": "rgba(248, 113, 113, 1)",
        "modalBackdropBackground":                 "rgba(15, 23, 42, 0.55)",
        "modalCardBackground":                     "rgba(22, 47, 86, 1)",
        "modalCardBorder":                         "rgba(255, 255, 255, 0.14)",
        "modalCardTextPrimary":                    "#F8FAFC",
        "swissPassButtonBackground":               "rgba(197, 20, 22, 1)",
        "swissPassButtonBackgroundHover":          "rgba(175, 22, 2, 1)",
        "controlThumb":                            "#FFFFFF",
    },
};

export const getUIColorPalette = ( isDarkMode: boolean ): UIColorPalette => {
    return isDarkMode ? colorPalette.dark : colorPalette.light;
};
